import { zodResolver } from "@hookform/resolvers/zod";
import { Save as SaveIcon } from "@mui/icons-material";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Snackbar,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  getFindAccessProfileQueryOptions,
  getListPermissionQueryOptions,
  getEditAccessProfileMutationOptions,
} from "../../../../../api/bpm/endpoints";
import type {
  AccessProfileWithAllPropertiesDto,
} from "../../../../../api/bpm/model";
import { useAuth } from "../../../../../auth";
import { AccessProfilePermissions } from "../../../../../components/Admin/AccessProfiles/AccessProfilePermissions";
import { extractAvailablePermissions } from "../../../../../utils/accessProfileTemplates";

export const Route = createFileRoute("/__auth/admin/profiles/$profileId/edit")({
  component: ProfileEditPage,
  staticData: {
    title: "routes.editProfile",
  },
});

const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().min(1, "Descrição é obrigatória"),
  permissions: z.array(z.number()),
});

type FormValues = z.infer<typeof schema>;

function ProfileEditPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profileId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "info" });
  };

  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    ...getFindAccessProfileQueryOptions(tenant, parseInt(profileId)),
  });

  const { data: allPermissionsData, isLoading: isLoadingAllPermissions } =
    useQuery({
      ...getListPermissionQueryOptions({
        size: 9999,
        page: 0,
        locale: "pt",
      }),
    });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
    },
  });

  useEffect(() => {
    if (profileData) {
      const p = profileData as AccessProfileWithAllPropertiesDto;
      reset({
        name: p.name || "",
        description: p.description || "",
        permissions: p.permissions?.map((per) => per.id!) || [],
      });
    }
  }, [profileData, reset]);

  const mutation = useMutation({
    ...getEditAccessProfileMutationOptions(),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: t("admin.profiles.updateSuccess"),
        severity: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["findAccessprofile"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getV1ByTenantAccessProfiles"],
      });
      setTimeout(() => {
        navigate({ to: "/admin/profiles", search: { page: 1, size: 10 } });
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: t("admin.profiles.updateError"),
        severity: "error",
      });
      console.error(error);
    },
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate({
      tenant,
      accessProfileId: parseInt(profileId),
      data: {
        name: data.name,
        description: data.description,
        permissions: data.permissions.map((id) => ({ id })),
      },
    });
  };

  const selectedPermissionIds = watch("permissions");

  const allPermissions = useMemo(
    () => extractAvailablePermissions(allPermissionsData),
    [allPermissionsData],
  );

  if (isLoadingProfile || isLoadingAllPermissions) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>{t("admin.profiles.editTitle")}         </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Dados do Perfil
        </Typography>

        <Stack spacing={2.5} sx={{ mb: 4 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do Perfil"
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("admin.common.description")}
                fullWidth
                size="small"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <AccessProfilePermissions
          permissions={allPermissions}
          selectedPermissionIds={selectedPermissionIds || []}
          onChange={(permissionIds) =>
            setValue("permissions", permissionIds, { shouldDirty: true })
          }
        />

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() =>
              navigate({ to: "/admin/profiles", search: { page: 1, size: 10 } })
            }
            disabled={mutation.isPending}
          >{t("common.cancel").toUpperCase()}</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={mutation.isPending}
          >{t("common.save").toUpperCase()}</Button>
        </Box>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
